// Vendor-agnostic cookie consent.
//
// Feature code calls `consent.get()` / `consent.set(...)` and never touches
// localStorage directly. Analytics adapters subscribe to changes so they can
// inject scripts the moment consent is granted (and skip injection otherwise).

const STORAGE_KEY = 'cookie-consent'

export type ConsentStatus = 'granted' | 'denied' | null

type Listener = (status: ConsentStatus) => void

const listeners = new Set<Listener>()

function readStored(): ConsentStatus {
  try {
    if (typeof window === 'undefined') return null
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw === 'granted' || raw === 'denied') return raw
    return null
  } catch {
    return null
  }
}

function writeStored(status: ConsentStatus): void {
  try {
    if (status === null) {
      window.localStorage.removeItem(STORAGE_KEY)
    } else {
      window.localStorage.setItem(STORAGE_KEY, status)
    }
  } catch {
    // Storage full / disabled - silent. Choice becomes session-only.
  }
}

/** Test-only: exercise readStored's SSR/no-window branch. */
export const __readStoredForTests = readStored

export const consent = {
  get(): ConsentStatus {
    return readStored()
  },

  set(status: ConsentStatus): void {
    writeStored(status)
    for (const fn of listeners) fn(status)
  },

  /** Subscribe to consent changes. Returns an unsubscribe function. */
  subscribe(fn: Listener): () => void {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  },

  /** Test-only: clear listeners and stored value between tests. */
  _resetForTesting(): void {
    listeners.clear()
    writeStored(null)
  },
}
